// Add-worker wizard form shape.
//
// One schema covering every step, with per-step subsets derived from it via
// `pick`. A single schema means one typed value and one source of truth; the
// subsets exist so Next can validate just the step in front of the operator
// rather than the whole form, which would let an untouched later field block
// progress.
//
// The name and tag rules come from the server schema so the browser and the route
// handler cannot disagree about what is valid.
//
// The SSH fields are a branch, not a second form: they are required only when the
// target is another machine. Expressing that as `.optional()` would accept a
// remote deploy with no host, so it is a refinement over the whole value instead —
// which still reports against the individual field, so the step validation and
// the error message land where the operator is looking.
//
// The credential is a branch inside that branch. `sshKeyId` names a key in the
// library and `privateKey` brings one along, and only one of them is ever
// required — which is why `privateKey` cannot simply be `.min(1)`.

import { WorkerImageTagSchema, WorkerNameSchema } from "@schemas/provision.ts";
import { z } from "zod";
import { privateKeyIssue } from "@/lib/private-key-shape";

/**
 * Where the worker comes from.
 *
 * `register` records a worker that is already running elsewhere, so it shares
 * the entry point with the two deploy paths — "add a worker" is one question and
 * it used to be two buttons in the corner of a table.
 */
export const DEPLOY_TARGETS = ["local", "remote", "register"] as const;
export type DeployTarget = (typeof DEPLOY_TARGETS)[number];

/**
 * The picker's value for "I am bringing my own key".
 *
 * An empty string rather than null, so the whole form stays strings and booleans
 * and a RadioGroup — which only speaks strings — can hold this directly.
 */
export const NEW_SSH_KEY = "" as const;

// Every field is required and every value is a string or a boolean, so the
// schema's input and output types are the same one. `.default()` and
// `z.coerce` would split them, and react-hook-form would then be resolving a
// shape that is not the shape it holds. Defaults live in
// DEPLOY_WORKER_DEFAULTS instead, and the port is parsed at submit.
const DeployWorkerFields = z.object({
	target: z.enum(DEPLOY_TARGETS),
	name: WorkerNameSchema,
	imageTag: WorkerImageTagSchema,

	// --- remote (SSH) target only ---
	//
	// Prefixed because "host" in a form that also names a worker is ambiguous:
	// one is the machine, the other is what the worker will be called.
	sshHost: z.string().trim(),
	sshPort: z
		.string()
		.trim()
		.regex(/^\d{1,5}$/, "Give a port number")
		.refine((v) => {
			const port = Number(v);
			return port >= 1 && port <= 65535;
		}, "Ports go from 1 to 65535"),
	sshUser: z.string().trim(),

	// --- the credential, one of two ways ---
	//
	/**
	 * A key from the library, by id. `NEW_SSH_KEY` means the operator is pasting
	 * one instead, and is what makes the three fields below relevant.
	 *
	 * Picking a saved key is the path where the secret never enters the browser:
	 * no endpoint returns key material, so this id is the only handle there is.
	 */
	sshKeyId: z.string(),
	/** PEM text. Empty until the operator pastes or drops a key file. */
	privateKey: z.string(),
	/** Empty means the key is not encrypted. */
	passphrase: z.string(),
	/** Also add the pasted key to the library, under `keyName`. */
	saveKey: z.boolean(),
	/** What the saved key is called. Only read when `saveKey` is on. */
	keyName: z.string().trim(),
	/** Pipe get.docker.com into root's shell. Explicit opt-in, never a default. */
	installDocker: z.boolean(),
	restrictFirewall: z.boolean(),
	autoUpdate: z.boolean(),
});

export const DeployWorkerSchema = DeployWorkerFields.superRefine((v, ctx) => {
	if (v.target !== "remote") return;
	if (v.sshHost.length === 0) {
		ctx.addIssue({
			code: "custom",
			path: ["sshHost"],
			message: "Give the machine an address",
		});
	}
	if (v.sshUser.length === 0) {
		ctx.addIssue({
			code: "custom",
			path: ["sshUser"],
			message: "Give the SSH user",
		});
	}
	// A saved key needs nothing else: the manager already holds the material and
	// this form could not check it if it wanted to.
	if (v.sshKeyId !== NEW_SSH_KEY) return;

	const issue = privateKeyIssue(v.privateKey);
	if (issue) {
		ctx.addIssue({ code: "custom", path: ["privateKey"], message: issue });
	}
	if (v.saveKey && v.keyName.length === 0) {
		ctx.addIssue({
			code: "custom",
			path: ["keyName"],
			message: "Name the key so you can pick it next time",
		});
	}
});

export type DeployWorkerValue = z.infer<typeof DeployWorkerSchema>;

/** Fields each step owns, for per-step validation. */
export const STEP_FIELDS = {
	target: ["target"],
	configure: ["name", "imageTag"],
	connection: [
		"sshHost",
		"sshPort",
		"sshUser",
		"sshKeyId",
		"privateKey",
		"passphrase",
		"keyName",
	],
	register: [],
	review: [],
} as const satisfies Record<string, readonly (keyof DeployWorkerValue)[]>;

/** The fields Test connection needs before it is worth spending ten seconds. */
export const CONNECTION_PROBE_FIELDS = [
	"sshHost",
	"sshPort",
	"sshUser",
	"sshKeyId",
	"privateKey",
] as const satisfies readonly (keyof DeployWorkerValue)[];

export const TargetStepSchema = DeployWorkerFields.pick({ target: true });
export const ConfigureStepSchema = DeployWorkerFields.pick({
	name: true,
	imageTag: true,
});

/** Default values for a fresh wizard. */
export const DEPLOY_WORKER_DEFAULTS: DeployWorkerValue = {
	target: "local",
	name: "",
	imageTag: "latest",
	sshHost: "",
	sshPort: "22",
	sshUser: "root",
	sshKeyId: NEW_SSH_KEY,
	privateKey: "",
	passphrase: "",
	saveKey: false,
	keyName: "",
	installDocker: false,
	restrictFirewall: false,
	autoUpdate: false,
};

/** Where the machine is, without the credential. */
export interface SshEndpointFields {
	host: string;
	port: number;
	user: string;
}

/** How the request names its key — the union both remote routes accept. */
export type SshKeyFields =
	| { sshKeyId: string }
	| { privateKey: string; passphrase: string | null; saveAs?: string };

/**
 * The SSH half of the form, in the shape both remote routes take.
 *
 * One place builds it so the Test connection button and the deploy submit cannot
 * test one host and deploy to another, or test a saved key and deploy with a
 * pasted one — and so an empty passphrase becomes null rather than an empty
 * string the server would try to feed to askpass.
 */
export function toSshRequest(
	values: DeployWorkerValue,
): SshEndpointFields & SshKeyFields {
	const endpoint: SshEndpointFields = {
		host: values.sshHost.trim(),
		port: Number(values.sshPort),
		user: values.sshUser.trim(),
	};
	if (values.sshKeyId !== NEW_SSH_KEY) {
		return { ...endpoint, sshKeyId: values.sshKeyId };
	}
	return {
		...endpoint,
		privateKey: values.privateKey,
		passphrase: values.passphrase.length > 0 ? values.passphrase : null,
		...(values.saveKey && values.keyName.length > 0
			? { saveAs: values.keyName }
			: {}),
	};
}
